// =============================================================================
// FILE: TicketAIChatPanel.js
// UPDATED: 2025-12-14
// PURPOSE:
//   Ticket AI Chat Panel with Minimize / Maximize / Fullscreen support
// =============================================================================

import React, { useState } from "react";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import TicketConfirmationPanel from "./TicketConfirmationPanel";
import axiosClient from "../../../../api/axiosClient";

export default function TicketAIChatPanel({ ticketId = null, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  // 🔹 UI state
  const [viewMode, setViewMode] = useState("normal"); 
  // normal | minimized | fullscreen

  const sendMessage = async (text) => {
    if (!text || loading) return;

    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "user", text, timestamp: new Date() },
      { id: "typing", role: "assistant", typing: true },
    ]);

    setLoading(true);

    try {
      const payload = { message: text };
      if (ticketId) payload.ticket_id = ticketId;
      if (conversationId) payload.conversation_id = conversationId;

      const res = await axiosClient.post("/ticket-ai/chat", payload);
      const data = res.data;

      if (!data.success) throw new Error("AI error");

      if (data.conversation_id) setConversationId(data.conversation_id);

      setMessages((prev) =>
        prev
          .filter((m) => m.id !== "typing")
          .concat({
            id: Date.now() + 1,
            role: "assistant",
            text: data.reply || "",
            timestamp: new Date(),
            confirmation_required: data.confirmation_required === true,
            meta: { followup_questions: data.followup_questions || [] },
          })
      );
    } catch (err) {
      setMessages((prev) =>
        prev
          .filter((m) => m.id !== "typing")
          .concat({
            id: Date.now() + 2,
            role: "assistant",
            text: "Sorry, something went wrong.",
            timestamp: new Date(),
          })
      );
    } finally {
      setLoading(false);
    }
  };

  const lastMessage =
    messages.length > 0 ? messages[messages.length - 1] : null;

  return (
    <div style={getContainerStyle(viewMode)}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>🤖 Ticket Assistant</div>

        <div style={styles.headerActions}>
          {/* Minimize */}
          <button
            style={styles.iconBtn}
            title="Minimize"
            onClick={() => setViewMode("minimized")}
          >
            ➖
          </button>

          {/* Maximize */}
          <button
            style={styles.iconBtn}
            title="Restore"
            onClick={() => setViewMode("normal")}
          >
            🗖
          </button>

          {/* Fullscreen */}
          <button
            style={styles.iconBtn}
            title="Fullscreen"
            onClick={() => setViewMode("fullscreen")}
          >
            ⛶
          </button>

          {/* Close */}
          {onClose && (
            <button
              style={{ ...styles.iconBtn, fontSize: "18px" }}
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Body (hidden when minimized) */}
      {viewMode !== "minimized" && (
        <>
          <div style={styles.messages}>
            <MessageList messages={messages} onChipClick={sendMessage} />
          </div>

          {lastMessage?.confirmation_required && (
            <TicketConfirmationPanel
              onConfirm={() => sendMessage("yes")}
              onReject={() => sendMessage("no")}
            />
          )}

          <div style={styles.input}>
            <MessageInput onSend={sendMessage} disabled={loading} />
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dynamic Container Style */
/* ------------------------------------------------------------------ */
function getContainerStyle(viewMode) {
  if (viewMode === "fullscreen") {
    return {
      ...styles.container,
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      borderRadius: 0,
    };
  }

  if (viewMode === "minimized") {
    return {
      ...styles.container,
      height: "52px",
      overflow: "hidden",
    };
  }

  return styles.container;
}

/* ------------------------------------------------------------------ */
/* Styles */
/* ------------------------------------------------------------------ */
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px",
    background: "linear-gradient(135deg, #7c3aed, #ec4899)",
    color: "#fff",
  },

  headerTitle: {
    fontSize: "15px",
    fontWeight: 600,
  },

  headerActions: {
    display: "flex",
    gap: "6px",
  },

  iconBtn: {
    background: "transparent",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    lineHeight: 1,
  },

  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "12px",
  },

  input: {
    borderTop: "1px solid #e5e7eb",
    padding: "8px",
  },
};
